import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertActionCloseButton,
  AlertGroup,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  Checkbox,
  Content,
  DatePicker,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Divider,
  Drawer,
  DrawerActions,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentBody,
  DrawerHead,
  DrawerPanelBody,
  DrawerPanelContent,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  Icon,
  InputGroup,
  InputGroupItem,
  Label,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Radio,
  Select,
  SelectList,
  SelectOption,
  Stack,
  StackItem,
  TextArea,
  TextInput,
  Title,
  Tooltip,
  useWizardContext,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { CheckCircleIcon, ExclamationTriangleIcon, InfoCircleIcon, PencilAltIcon, PlusIcon, TimesIcon, CloudSecurityIcon, ServerGroupIcon, VirtualMachineIcon, QuestionCircleIcon, EyeIcon, EyeSlashIcon, ExternalLinkAltIcon, BuilderImageIcon } from '@patternfly/react-icons';
import { mockRepositories, addRepository } from '../data/repositoryData';
import { mockEnrollmentCertificates, EnrollmentCertificate } from '../data/enrollmentCertData';
import { ImageBuild } from '../data/imageBuildData';
import { getImageBuildsBasePath } from '@app/utils/appContext';

// Helper function to format export format labels
const getExportFormatLabel = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return 'VMware Disk';
    case 'qcow2':
      return 'Virtual Disk';
    case 'qcow2-container':
      return 'Disk Container';
    case 'iso':
      return 'Installer Image';
    default:
      return format.toUpperCase();
  }
};

// Helper function to get export format title without acronyms
const getExportFormatTitle = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '.VMDK';
    case 'qcow2':
      return '.QCOW2';
    case 'qcow2-container':
      return '.QCOW2 (Container)';
    case 'iso':
      return '.ISO';
    default:
      return `.${format.toUpperCase()}`;
  }
};

// Helper function to sort export formats in the correct order: ISO, QCOW2 (Container), VMDK, QCOW2
const sortExportFormats = (formats: string[] | Set<string>): string[] => {
  const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
  const formatArray = Array.isArray(formats) ? formats : Array.from(formats);
  return formatArray.sort((a, b) => {
    const aIndex = formatOrder.indexOf(a.toLowerCase());
    const bIndex = formatOrder.indexOf(b.toLowerCase());
    // If format not found in order, put it at the end
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

// Helper function to get export format description
const getExportFormatDescription = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'iso':
      return 'For physical hardware, bare metal, and manual setups.';
    case 'qcow2-container':
      return 'For OpenShift Virtualization and cloud environments.';
    case 'vmdk':
      return 'For vSphere and enterprise VMWare platforms.';
    case 'qcow2':
      return 'For storing and pulling disks from container registries.';
    default:
      return '';
  }
};

// Helper function to get export format file size
const getExportFormatSize = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return '1 GB';
    case 'qcow2':
      return '1.1 GB';
    case 'iso':
      return '1.5 GB';
    default:
      return '';
  }
};

// Helper function to get export format tooltip content
const getExportFormatTooltipContent = (format: string): React.ReactNode => {
  const formatLower = format.toLowerCase();
  const size = getExportFormatSize(formatLower);
  
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        How to use this image (~20 min total)
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Download:</strong> Save the {size} file to your local drive.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Import:</strong> Open your VM software (e.g., VirtualBox) and select 'Import'.
      </div>
      <div style={{ marginBottom: '4px' }}>
        <strong>Boot:</strong> Power on the VM to complete the automated setup.
      </div>
      <div style={{ marginTop: '8px', fontStyle: 'italic', fontSize: 'var(--pf-t--global--font--size--sm)' }}>
        Note: Download speeds vary based on your network and registry access.
      </div>
    </div>
  );
};

// Helper function to get export format acronym
const DEFAULT_INPUT_IMAGE_NAME = 'RHEL 9.4';
const DEFAULT_INPUT_IMAGE_TAG = '9.4, latest';

const getInputImagePath = (imageName: string): string => {
  const key = imageName.trim().toLowerCase();
  if (key === 'rhel 9.4' || key.startsWith('rhel 9')) {
    return 'rhel9/rhel-bootc';
  }
  return imageName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9./_-]/g, '');
};

const getInputImageReferenceUrl = (
  registry: { url: string } | null,
  imageName: string,
  tag: string,
): string => {
  if (!registry) {
    return '';
  }
  const host = registry.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!imageName.trim() || !tag.trim()) {
    return host;
  }
  const primaryTag = tag.split(',')[0].trim();
  return `${host}/${getInputImagePath(imageName)}:${primaryTag}`;
};

const applyRhelInputDefaults = (repositoryName: string): { imageName: string; tag: string } | null => {
  const key = repositoryName.toLowerCase();
  if (key.includes('rhel') || key.includes('rhel-bootc')) {
    return { imageName: DEFAULT_INPUT_IMAGE_NAME, tag: DEFAULT_INPUT_IMAGE_TAG };
  }
  return null;
};

const getExportFormatAcronym = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return 'VMDK';
    case 'qcow2':
      return 'QCOW2';
    case 'iso':
      return 'ISO';
    default:
      return format.toUpperCase();
  }
};

// Review Step Content Component - needs to be inside Wizard to use useWizardContext
const ReviewStepContent: React.FunctionComponent<{
  registryName: string;
  selectedRegistry: { url: string; credential?: string } | null;
  imageName: string;
  tag: string;
  outputRepository: string;
  selectedOutputRegistry: { url: string; credential?: string } | null;
  outputImageName: string;
  outputTag: string;
  autoGenerateTag: boolean;
  getOutputImagePreview: () => string;
  exportFormats: Set<string>;
  includeFlightControlAgent: boolean;
  bindingType: 'early' | 'late';
  earlyRegistrationOption: 'existing' | 'autocreate';
  autoCreateCert: boolean;
  cert: string;
  selectedCertificate: EnrollmentCertificate | null;
  durationType: '365' | 'custom';
  customExpirationDate: Date | null;
  ttl: string;
  formatExpirationDate: (dateString: string) => string;
}> = ({
  registryName,
  selectedRegistry,
  imageName,
  tag,
  outputRepository,
  selectedOutputRegistry,
  outputImageName,
  outputTag,
  autoGenerateTag,
  getOutputImagePreview,
  exportFormats,
  includeFlightControlAgent,
  bindingType,
  earlyRegistrationOption,
  autoCreateCert,
  cert,
  selectedCertificate,
  durationType,
  customExpirationDate,
  ttl,
  formatExpirationDate,
}) => {
  const { goToStepById } = useWizardContext();

  const inputReferenceUrl = getInputImageReferenceUrl(selectedRegistry, imageName, tag);

  return (
    <div className="form-container">
    <Stack hasGutter>
      <StackItem>
        <Title headingLevel="h2" size="lg" className="form-section-title">Review</Title>
        <Content component="p" className="form-section-description" style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
          Review your image build configuration before creating.
        </Content>
      </StackItem>

      {/* Base Image Summary */}
      <StackItem>
        <Card className="app-review-card">
          <CardTitle>
            Input image
          </CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isFluid className="app-review-description-list">
              <DescriptionListGroup>
                <DescriptionListTerm>Input image name</DescriptionListTerm>
                <DescriptionListDescription>{imageName || 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Image input source repository</DescriptionListTerm>
                <DescriptionListDescription>{registryName ? registryName : 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Input image tag</DescriptionListTerm>
                <DescriptionListDescription>{tag || 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Input image reference URL</DescriptionListTerm>
                <DescriptionListDescription>
                  {inputReferenceUrl
                    ? (
                        <a
                          href={`https://${inputReferenceUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0066cc',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            wordBreak: 'break-word',
                          }}
                        >
                          {inputReferenceUrl}
                          <ExternalLinkAltIcon style={{ fontSize: '0.875rem', color: '#0066cc', flexShrink: 0 }} />
                        </a>
                      )
                    : <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Not set</span>}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </StackItem>

      {/* Image Output Summary */}
      <StackItem>
        <Card className="app-review-card">
          <CardTitle>
            Output image
          </CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isFluid className="app-review-description-list">
              <DescriptionListGroup>
                <DescriptionListTerm>Target repository</DescriptionListTerm>
                <DescriptionListDescription>{outputRepository || 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Output image name</DescriptionListTerm>
                <DescriptionListDescription>{outputImageName || 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Output image tag</DescriptionListTerm>
                <DescriptionListDescription>{outputTag || 'Not set'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Export formats</DescriptionListTerm>
                <DescriptionListDescription>
                  {exportFormats.size > 0
                    ? (
                        <Flex spaceItems={{ default: 'spaceItemsSm' }} flexWrap={{ default: 'wrap' }}>
                          {sortExportFormats(Array.from(exportFormats)).map((format, idx) => (
                            <FlexItem key={idx}>
                              <Label color="blue">
                                {getExportFormatTitle(format.toLowerCase())}
                              </Label>
                            </FlexItem>
                          ))}
                        </Flex>
                      )
                    : 'None'}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </StackItem>

      {/* Registration Summary */}
      <StackItem>
        <Card className="app-review-card">
          <CardTitle>
            Registration
          </CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isFluid className="app-review-description-list">
              <DescriptionListGroup>
                <DescriptionListTerm>Binding</DescriptionListTerm>
                <DescriptionListDescription>{bindingType === 'early' ? 'Early binding' : 'Late binding'}</DescriptionListDescription>
              </DescriptionListGroup>
              {bindingType === 'early' && (
                <>
                  <DescriptionListGroup>
                    <DescriptionListTerm>Enrollment</DescriptionListTerm>
                    <DescriptionListDescription>Auto-create certificate</DescriptionListDescription>
                  </DescriptionListGroup>
                  <DescriptionListGroup>
                    <DescriptionListTerm>Registration</DescriptionListTerm>
                    <DescriptionListDescription>
                      {durationType === '365' ? '1 year (Standard)' :
                       durationType === 'custom' && customExpirationDate ? formatExpirationDate(customExpirationDate.toISOString()) :
                       ttl || 'Not set'}
                    </DescriptionListDescription>
                  </DescriptionListGroup>
                </>
              )}
              {bindingType === 'late' && (
                <DescriptionListGroup>
                  <DescriptionListTerm>Registration</DescriptionListTerm>
                  <DescriptionListDescription>
                    <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsFlexStart' }}>
                      <FlexItem>
                        <Icon status="info">
                          <InfoCircleIcon />
                        </Icon>
                      </FlexItem>
                      <FlexItem>
                        Provisioning tools are enabled by default.
                      </FlexItem>
                    </Flex>
                  </DescriptionListDescription>
                </DescriptionListGroup>
              )}
            </DescriptionList>
          </CardBody>
        </Card>
      </StackItem>
    </Stack>
    </div>
  );
};

const CreateImageBuild: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Helper function to get the correct base path based on current location
  const getBasePath = () => getImageBuildsBasePath(location.pathname);
  
  const [currentStep, setCurrentStep] = React.useState<string>('step-base-image');
  const [showBuildStartedModal, setShowBuildStartedModal] = React.useState(false);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = React.useState(false);

  // Base image fields
  const [registryName, setRegistryName] = React.useState('RHEL 9.4 - x86_64');
  const [selectedRegistry, setSelectedRegistry] = React.useState<{ url: string; credential?: string } | null>(() => {
    const defaultRepo = mockRepositories.find(repo => repo.name === 'RHEL 9.4 - x86_64' && repo.type === 'OCI registry');
    return defaultRepo ? {
      url: defaultRepo.url,
      credential: defaultRepo.credential,
    } : null;
  });
  const [imageName, setImageName] = React.useState(DEFAULT_INPUT_IMAGE_NAME);
  const [tag, setTag] = React.useState(DEFAULT_INPUT_IMAGE_TAG);
  const [baseImageCheckbox, setBaseImageCheckbox] = React.useState(true);
  
  // Red Hat Registry credentials
  const [redHatRegistry, setRedHatRegistry] = React.useState('');
  const [redHatAuthType, setRedHatAuthType] = React.useState<string>('');
  const [isRedHatAuthTypeOpen, setIsRedHatAuthTypeOpen] = React.useState(false);
  const [redHatUsername, setRedHatUsername] = React.useState('');
  const [redHatPassword, setRedHatPassword] = React.useState('');
  const [redHatAuthError, setRedHatAuthError] = React.useState(false);
  const [showRedHatPassword, setShowRedHatPassword] = React.useState(false);
  const [showRegistryConnectedToast, setShowRegistryConnectedToast] = React.useState(false);
  const [connectedRegistryName, setConnectedRegistryName] = React.useState<string>('');

  // Image output fields
  const [outputRepository, setOutputRepository] = React.useState('');
  const [selectedOutputRegistry, setSelectedOutputRegistry] = React.useState<{ url: string; credential?: string } | null>(null);
  const [outputImageName, setOutputImageName] = React.useState('');
  const [outputTag, setOutputTag] = React.useState('');
  const [autoGenerateTag, setAutoGenerateTag] = React.useState(false);
  const [exportFormats, setExportFormats] = React.useState<Set<string>>(new Set());
  const [includeFlightControlAgent, setIncludeFlightControlAgent] = React.useState(true);
  
  // Output repository dropdown state
  const [isOutputRepositoryDropdownOpen, setIsOutputRepositoryDropdownOpen] = React.useState(false);
  
  // User permissions (mock - in real app, this would come from auth context)
  const [hasAdminPermission] = React.useState(true);

  // Registration fields
  const [bindingType, setBindingType] = React.useState<'early' | 'late'>('early');
  
  // SSH public key fields
  const [enableSshAccess, setEnableSshAccess] = React.useState(false);
  const [sshPublicKey, setSshPublicKey] = React.useState('');
  const [sshKeyFileName, setSshKeyFileName] = React.useState('');
  const [earlyRegistrationOption, setEarlyRegistrationOption] = React.useState<'existing' | 'autocreate'>('autocreate');
  const [autoCreateCert, setAutoCreateCert] = React.useState(true);
  const [cert, setCert] = React.useState('');
  const [ttl, setTtl] = React.useState('');
  const [selectedCertificate, setSelectedCertificate] = React.useState<EnrollmentCertificate | null>(null);
  
  // Certificate duration fields
  const [durationType, setDurationType] = React.useState<'365' | 'custom'>('365');
  const [customExpirationDate, setCustomExpirationDate] = React.useState<Date | null>(null);
  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = React.useState(false);

  // Dropdown states
  const [isRegistryDropdownOpen, setIsRegistryDropdownOpen] = React.useState(false);
  const [isCertDropdownOpen, setIsCertDropdownOpen] = React.useState(false);
  const [showCreateRegistryModal, setShowCreateRegistryModal] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showCancelRegistryModal, setShowCancelRegistryModal] = React.useState(false);
  const [showRegistrySuccessModal, setShowRegistrySuccessModal] = React.useState(false);
  const [pendingRegistrySelection, setPendingRegistrySelection] = React.useState<string | null>(null);
  const [showRedHatRegistryModal, setShowRedHatRegistryModal] = React.useState(false);
  
  // Output repository create flow states
  const [showCreateOutputRegistryModal, setShowCreateOutputRegistryModal] = React.useState(false);
  const [showConfirmOutputRepositoryModal, setShowConfirmOutputRepositoryModal] = React.useState(false);
  const [showCancelOutputRepositoryModal, setShowCancelOutputRepositoryModal] = React.useState(false);
  const [showOutputRegistrySuccessModal, setShowOutputRegistrySuccessModal] = React.useState(false);
  const [pendingOutputRepositorySelection, setPendingOutputRepositorySelection] = React.useState<string | null>(null);
  const [createdRegistryName, setCreatedRegistryName] = React.useState<string>('');
  const [createdOutputRegistryName, setCreatedOutputRegistryName] = React.useState<string>('');
  
  // Output repository form fields (reuse same fields as input registry)
  const [newOutputRegistryName, setNewOutputRegistryName] = React.useState('');
  const [newOutputRegistryHostname, setNewOutputRegistryHostname] = React.useState('');
  const [newOutputRegistryType, setNewOutputRegistryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [newOutputScheme, setNewOutputScheme] = React.useState<'https' | 'http'>('https');
  const [newOutputAccessMode, setNewOutputAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [newOutputUseBasicAuth, setNewOutputUseBasicAuth] = React.useState(false);
  const [newOutputUsername, setNewOutputUsername] = React.useState('');
  const [newOutputPassword, setNewOutputPassword] = React.useState('');
  const [newOutputSkipServerVerification, setNewOutputSkipServerVerification] = React.useState(false);
  const [newOutputCaCertificate, setNewOutputCaCertificate] = React.useState('');

  // Create registry form fields
  const [newRegistryName, setNewRegistryName] = React.useState('');
  const [newRegistryHostname, setNewRegistryHostname] = React.useState('');
  const [newRegistryType, setNewRegistryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [newScheme, setNewScheme] = React.useState<'https' | 'http'>('https');
  const [newAccessMode, setNewAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [newUseBasicAuth, setNewUseBasicAuth] = React.useState(false);
  const [newUsername, setNewUsername] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [newSkipServerVerification, setNewSkipServerVerification] = React.useState(false);
  const [newCaCertificate, setNewCaCertificate] = React.useState('');

  // Get OCI repositories for registry dropdown
  const [ociRepositories, setOciRepositories] = React.useState(() => {
    // Filter and sort to ensure consistent order
    const repos = mockRepositories.filter(repo => repo.type === 'OCI registry');
    // Sort by name to ensure consistent ordering
    return repos.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Prefill form from retry build data
  React.useEffect(() => {
    const retryBuild = (location.state as { retryBuild?: ImageBuild })?.retryBuild;
    if (retryBuild) {
      // Parse inputImage (e.g., "rhel9/rhel-bootc:9.4")
      if (retryBuild.inputImage) {
        const inputImageParts = retryBuild.inputImage.split(':');
        const inputImageName = inputImageParts[0];
        const inputTag = inputImageParts[1] || '';
        
        // Try to find matching registry name
        const matchingRepo = mockRepositories.find(repo => 
          repo.type === 'OCI registry' && 
          (repo.name === inputImageName || repo.name.toLowerCase().includes(inputImageName.toLowerCase()))
        );
        
        if (matchingRepo) {
          setRegistryName(matchingRepo.name);
          setSelectedRegistry({
            url: matchingRepo.url,
            credential: matchingRepo.credential,
          });
        } else {
          // Fallback: use the inputImage name as-is
          setRegistryName(inputImageName);
        }
        
        if (inputTag) {
          setTag(inputTag);
        }
      }
      
      // Parse outputImage (e.g., "my-edge-img:v1.0.0")
      if (retryBuild.outputImage) {
        const outputImageParts = retryBuild.outputImage.split(':');
        const outputImageName = outputImageParts[0];
        const outputTag = outputImageParts[1] || '';
        
        setOutputImageName(outputImageName);
        if (outputTag) {
          setOutputTag(outputTag);
          setAutoGenerateTag(false);
        }
      }
      
      // Parse registryUrl to extract output registry (e.g., "quay.io/myorg/my-edge-img:v1.0.0")
      if (retryBuild.registryUrl) {
        const registryUrlParts = retryBuild.registryUrl.split('/');
        if (registryUrlParts.length > 0) {
          const registryHost = registryUrlParts[0];
          const outputRepo = mockRepositories.find(repo => 
            repo.type === 'OCI registry' && 
            repo.url.includes(registryHost)
          );
          
          if (outputRepo) {
            setOutputRepository(outputRepo.name);
            setSelectedOutputRegistry({
              url: outputRepo.url,
              credential: outputRepo.credential,
            });
          }
        }
      }
      
      // Set export formats
      if (retryBuild.exportFormats && retryBuild.exportFormats.length > 0) {
        setExportFormats(new Set(retryBuild.exportFormats));
      }
      
      // Set binding type
      if (retryBuild.binding) {
        setBindingType(retryBuild.binding);
      }
    }
  }, [location.state]);

  const handleCancel = () => {
    navigate(getBasePath());
  };

  const handleSaveRegistry = () => {
    // Only allow OCI registry type for image builds
    if (newRegistryType !== 'oci') {
      return;
    }
    
    // Create new repository
    const newRepo = addRepository({
      name: newRegistryName,
      type: 'OCI registry',
      url: `${newScheme}://${newRegistryHostname}`,
      syncStatus: 'Accessible',
      lastTransition: new Date().toISOString(),
      credential: newUseBasicAuth ? `${newRegistryName}-credentials` : undefined,
    });
    
    // Update OCI repositories list
    setOciRepositories(prev => [...prev, newRepo]);
    
    // Store name for success message
    setCreatedRegistryName(newRegistryName);
    
    // Select the new registry
    setRegistryName(newRegistryName);
    setSelectedRegistry({
      url: `${newScheme}://${newRegistryHostname}`,
      credential: newUseBasicAuth ? 'basic-auth-credentials' : undefined,
    });
    
    // Reset form, close modal, and show success
    resetRegistryForm();
    setShowCreateRegistryModal(false);
    setShowRegistrySuccessModal(true);
  };

  const handleCancelRegistry = () => {
    // Check if there's any data entered
    const hasData = newRegistryName.trim() !== '' || 
                    newRegistryHostname.trim() !== '' || 
                    newUsername.trim() !== '' || 
                    newPassword.trim() !== '' || 
                    newCaCertificate.trim() !== '';
    
    if (hasData) {
      setShowCancelRegistryModal(true);
    } else {
      setShowCreateRegistryModal(false);
      resetRegistryForm();
    }
  };

  const resetRegistryForm = () => {
    setNewRegistryName('');
    setNewRegistryHostname('');
    setNewRegistryType('oci');
    setNewScheme('https');
    setNewAccessMode('readonly');
    setNewUseBasicAuth(false);
    setNewUsername('');
    setNewPassword('');
    setNewSkipServerVerification(false);
    setNewCaCertificate('');
  };

  const handleConfirmCancelRegistry = () => {
    setShowCreateRegistryModal(false);
    resetRegistryForm();
    setShowCancelRegistryModal(false);
  };

  const handleKeepCreatingRegistry = () => {
    setShowCancelRegistryModal(false);
  };

  const handleConfirmSelectRegistry = () => {
    if (pendingRegistrySelection) {
      const selectedRepo = ociRepositories.find(repo => repo.name === pendingRegistrySelection);
      setRegistryName(pendingRegistrySelection);
      if (selectedRepo) {
        setSelectedRegistry({
          url: selectedRepo.url,
          credential: selectedRepo.credential,
        });
      }
      // Close create registry modal and clear its data
      setShowCreateRegistryModal(false);
      resetRegistryForm();
    }
    setShowConfirmModal(false);
    setPendingRegistrySelection(null);
  };

  const handleCancelSelectRegistry = () => {
    setShowConfirmModal(false);
    setPendingRegistrySelection(null);
  };

  // Output repository handlers
  const handleSaveOutputRegistry = () => {
    // Only allow OCI registry type for image builds
    if (newOutputRegistryType !== 'oci') {
      return;
    }
    
    // Create new repository
    const newRepo = addRepository({
      name: newOutputRegistryName,
      type: 'OCI registry',
      url: `${newOutputScheme}://${newOutputRegistryHostname}`,
      syncStatus: 'Accessible',
      lastTransition: new Date().toISOString(),
      credential: newOutputUseBasicAuth ? `${newOutputRegistryName}-credentials` : undefined,
    });
    
    // Update OCI repositories list
    setOciRepositories(prev => [...prev, newRepo]);
    
    // Store name for success message
    setCreatedOutputRegistryName(newOutputRegistryName);
    
    // Select the new output repository
    setOutputRepository(newOutputRegistryName);
    setSelectedOutputRegistry({
      url: `${newOutputScheme}://${newOutputRegistryHostname}`,
      credential: newOutputUseBasicAuth ? `${newOutputRegistryName}-credentials` : undefined,
    });
    
    // Reset form, close modal, and show success
    resetOutputRegistryForm();
    setShowCreateOutputRegistryModal(false);
    setShowOutputRegistrySuccessModal(true);
  };

  const handleCancelOutputRegistry = () => {
    // Check if there's any data entered
    const hasData = newOutputRegistryName.trim() !== '' || 
                    newOutputRegistryHostname.trim() !== '' || 
                    newOutputUsername.trim() !== '' || 
                    newOutputPassword.trim() !== '' || 
                    newOutputCaCertificate.trim() !== '';
    
    if (hasData) {
      setShowCancelOutputRepositoryModal(true);
    } else {
      setShowCreateOutputRegistryModal(false);
      resetOutputRegistryForm();
    }
  };

  const resetOutputRegistryForm = () => {
    setNewOutputRegistryName('');
    setNewOutputRegistryHostname('');
    setNewOutputRegistryType('oci');
    setNewOutputScheme('https');
    setNewOutputAccessMode('readonly');
    setNewOutputUseBasicAuth(false);
    setNewOutputUsername('');
    setNewOutputPassword('');
    setNewOutputSkipServerVerification(false);
    setNewOutputCaCertificate('');
  };

  const handleConfirmCancelOutputRegistry = () => {
    setShowCreateOutputRegistryModal(false);
    resetOutputRegistryForm();
    setShowCancelOutputRepositoryModal(false);
  };

  const handleKeepCreatingOutputRegistry = () => {
    setShowCancelOutputRepositoryModal(false);
  };

  const handleConfirmSelectOutputRepository = () => {
    if (pendingOutputRepositorySelection) {
      const selectedRepo = ociRepositories.find(repo => repo.name === pendingOutputRepositorySelection);
      setOutputRepository(pendingOutputRepositorySelection);
      if (selectedRepo) {
        setSelectedOutputRegistry({
          url: selectedRepo.url,
          credential: selectedRepo.credential,
        });
      }
      // Close create output registry modal and clear its data
      setShowCreateOutputRegistryModal(false);
      resetOutputRegistryForm();
    }
    setShowConfirmOutputRepositoryModal(false);
    setPendingOutputRepositorySelection(null);
  };

  const handleCancelSelectOutputRepository = () => {
    setShowConfirmOutputRepositoryModal(false);
    setPendingOutputRepositorySelection(null);
  };

  const isNewOutputRegistryValid = newOutputRegistryName.trim() !== '' && newOutputRegistryHostname.trim() !== '';

  const handleSave = () => {
    // TODO: Implement save logic - create the image build
    // Show success modal
    setShowBuildStartedModal(true);
  };

  const handleViewBuildStatus = () => {
    setShowBuildStartedModal(false);
    navigate(getBasePath());
  };

  const handleBuildAnotherImage = () => {
    setShowBuildStartedModal(false);
    // Reset form to start a new build
    setCurrentStep('step-base-image');
    setRegistryName('');
    setSelectedRegistry(null);
    setImageName('');
    setTag('');
    setOutputRepository('');
    setSelectedOutputRegistry(null);
    setOutputImageName('');
    setOutputTag('');
    setAutoGenerateTag(false);
    setExportFormats(new Set());
    setIncludeFlightControlAgent(true);
    setBindingType('early');
    setEarlyRegistrationOption('existing');
    setAutoCreateCert(false);
    setCert('');
    setTtl('');
    setSelectedCertificate(null);
    setDurationType('365');
    setCustomExpirationDate(null);
  };

  const handleCloseBuildStartedModal = () => {
    setShowBuildStartedModal(false);
    navigate(getBasePath());
  };

  const isNewRegistryValid = newRegistryName.trim() !== '' && newRegistryHostname.trim() !== '';

  // Validation
  const isRegistryNameValid = registryName.trim() !== '';
  const isImageNameValid = imageName.trim() !== '';
  const isTagValid = tag.trim() !== '';
  const isOutputImageNameValid = outputImageName.trim() === '' || /^[a-z0-9-]+$/.test(outputImageName);
  const isOutputValid = outputRepository.trim() !== '' && 
                        outputImageName.trim() !== '' && 
                        isOutputImageNameValid &&
                        (autoGenerateTag || outputTag.trim() !== '');
  // Validate custom expiration date (cannot exceed 398 days)
  const isCustomDateValid = () => {
    if (durationType !== 'custom' || !customExpirationDate) {
      return true; // Not applicable
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiration = new Date(customExpirationDate);
    expiration.setHours(0, 0, 0, 0);
    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 398;
  };

  const isCertValid = bindingType === 'late' || autoCreateCert || cert.trim() !== '';
  const isTtlValid = bindingType === 'late' || !autoCreateCert || (autoCreateCert && ttl.trim() !== '' && (durationType !== 'custom' || isCustomDateValid()));

  const isFormValid = isRegistryNameValid && isImageNameValid && isTagValid && isOutputValid && isCertValid && isTtlValid;

  const handleExportFormatChange = (format: string, checked: boolean) => {
    setExportFormats(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(format);
      } else {
        newSet.delete(format);
      }
      return newSet;
    });
  };


  const registryToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsRegistryDropdownOpen(!isRegistryDropdownOpen)}
      isExpanded={isRegistryDropdownOpen}
      style={{ width: '100%' }}
      aria-label="Select a repository"
    >
      {registryName ? registryName.toLowerCase() : 'Select a repository'}
    </MenuToggle>
  );

  const certToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => {
        if (!autoCreateCert) {
          setIsCertDropdownOpen(!isCertDropdownOpen);
        }
      }}
      isExpanded={isCertDropdownOpen}
      isDisabled={autoCreateCert}
      style={{ width: '100%' }}
      aria-label="Select a certificate"
    >
      {cert || 'Select a certificate'}
    </MenuToggle>
  );

  const outputRepositoryToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsOutputRepositoryDropdownOpen(!isOutputRepositoryDropdownOpen)}
      isExpanded={isOutputRepositoryDropdownOpen}
      style={{ width: '100%' }}
      aria-label="Select a repository"
    >
      {outputRepository || 'Select a repository'}
    </MenuToggle>
  );

  // Generate preview of output image reference
  const getOutputImagePreview = () => {
    if (!selectedOutputRegistry || !outputImageName) {
      return '';
    }
    const registryUrl = selectedOutputRegistry.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const tag = autoGenerateTag ? 'auto' : (outputTag || 'latest');
    return `${registryUrl}/${outputImageName}:${tag}`;
  };

  // Format expiration date as "Month YYYY" (e.g., "Feb 2026")
  const formatExpirationDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if parsing fails
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      return dateString; // Return original if error occurs
    }
  };

  const durationToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsDurationDropdownOpen(!isDurationDropdownOpen)}
      isExpanded={isDurationDropdownOpen}
      style={{ width: '100%' }}
      aria-label="Select registration window"
    >
      {durationType === '365' ? '360 days (Recommended)' : (durationType === 'custom' && customExpirationDate ? formatExpirationDate(customExpirationDate.toISOString()) : 'Custom...')}
    </MenuToggle>
  );


  // Calculate TTL from duration type or custom date
  React.useEffect(() => {
    if (autoCreateCert) {
      if (durationType === '365') {
        setTtl('365d');
      } else if (durationType === 'custom' && customExpirationDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiration = new Date(customExpirationDate);
        expiration.setHours(0, 0, 0, 0);
        const diffTime = expiration.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0 && diffDays <= 398) {
          setTtl(`${diffDays}d`);
        } else {
          setTtl('');
        }
      } else {
        setTtl('');
      }
    }
  }, [autoCreateCert, durationType, customExpirationDate]);

  return (
    <>
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', marginBottom: 'var(--pf-t--global--spacer--md)' }}>
          <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
            <FlexItem>
              <Breadcrumb style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
                <BreadcrumbItem to={getBasePath()} onClick={(e) => { e.preventDefault(); navigate(getBasePath()); }}>
                  Image builds
                </BreadcrumbItem>
                <BreadcrumbItem isActive>Build image</BreadcrumbItem>
              </Breadcrumb>
              <Title headingLevel="h1" size="xl">
                Build image
              </Title>
              <Content component="p" style={{ color: 'var(--pf-t--global--text--color--subtle)', marginTop: 'var(--pf-t--global--spacer--xs)', marginBottom: 0 }}>
                Create and configure a new image build with your specified parameters
              </Content>
            </FlexItem>
          </Flex>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false}>
        <Drawer isExpanded={isHelpDrawerOpen}>
          <DrawerContent panelContent={
            <DrawerPanelContent>
              <DrawerHead>
                <Title headingLevel="h2" size="xl">Need help with these details?</Title>
                <DrawerActions>
                  <DrawerCloseButton onClick={() => setIsHelpDrawerOpen(false)} />
                </DrawerActions>
              </DrawerHead>
              <DrawerPanelBody>
                <Stack hasGutter>
                  <StackItem>
                    <Content>
                      To build your image, you must provide specific values from your external registry, such as Red Hat Quay.
                    </Content>
                  </StackItem>
                  <StackItem>
                    <Title headingLevel="h3" size="md">Finding your input image:</Title>
                  </StackItem>
                  <StackItem>
                    <Content>
                      <strong>Input image name:</strong> Provide accurate image name.
                    </Content>
                  </StackItem>
                  <StackItem>
                    <Content>
                      <strong>Image input source repository:</strong> Provide the full path to the image you want to use as an input (e.g., quay.io)
                    </Content>
                  </StackItem>
                  <StackItem>
                    <Content>
                      <strong>Input image tag:</strong> Look for the Tags tab in your registry to find the version label. Common tags include 9.6 or latest.
                    </Content>
                  </StackItem>
                  <StackItem>
                    <Content>
                      <strong>No repository configured?</strong> If you haven't set up a storage location for your images yet, you can create one now:
                    </Content>
                  </StackItem>
                  <StackItem>
                    <Button
                      variant="link"
                      isInline
                      icon={<PlusIcon />}
                      onClick={() => {
                        setIsHelpDrawerOpen(false);
                        setShowCreateRegistryModal(true);
                      }}
                    >
                      Create a repository
                    </Button>
                  </StackItem>
                </Stack>
              </DrawerPanelBody>
            </DrawerPanelContent>
          }>
            <DrawerContentBody>
              <Wizard
                className="app-image-build-wizard"
                height={600}
                onSave={handleSave}
                onStepChange={(_, step) => {
                  if (step.id) {
                    setCurrentStep(String(step.id));
                  }
                }}
              >
          {/* Step 1: Input image */}
          <WizardStep name="Input image" id="step-base-image" footer={{ isBackHidden: true }}>
            <div className="form-container">
              <Stack hasGutter>
                <StackItem>
                  <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                    <FlexItem>
                      <Title headingLevel="h2" size="lg" className="form-section-title">Input image</Title>
                    </FlexItem>
                  </Flex>
                  <Content component="p" className="form-section-description" style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
                    Define the registry and input image for your system image.
                  </Content>
                </StackItem>
                {/* Input image name */}
                <StackItem>
                  <FormGroup
                    label="Input image name"
                    isRequired
                    fieldId="image-name"
                  >
                    <div className="app-wizard-field">
                      <TextInput
                        id="image-name"
                        value={imageName}
                        onChange={(_, value) => setImageName(value)}
                        aria-describedby="image-name-helper"
                        validated={imageName && !isImageNameValid ? 'error' : 'default'}
                      />
                    </div>
                    <FormHelperText id="image-name-helper">
                      Provide accurate image name.
                    </FormHelperText>
                  </FormGroup>
                </StackItem>
                {/* Registry */}
                <StackItem>
                <FormGroup
                  label="Image input source repository"
                  isRequired
                  fieldId="registry-name"
                >
                  <Flex
                    alignItems={{ default: 'alignItemsCenter' }}
                    spaceItems={{ default: 'spaceItemsMd' }}
                    flexWrap={{ default: 'nowrap' }}
                  >
                    <FlexItem>
                      <div style={{ width: '400px' }}>
                        <Select
                          toggle={registryToggle}
                          onSelect={(_, value) => {
                            if (value) {
                              if (showCreateRegistryModal) {
                                setPendingRegistrySelection(value as string);
                                setShowConfirmModal(true);
                                setIsRegistryDropdownOpen(false);
                              } else {
                                const selectedRepo = ociRepositories.find(repo => repo.name === value);
                                setRegistryName(value as string);
                                if (selectedRepo) {
                                  setSelectedRegistry({
                                    url: selectedRepo.url,
                                    credential: selectedRepo.credential,
                                  });
                                }
                                const rhelDefaults = applyRhelInputDefaults(value as string);
                                if (rhelDefaults) {
                                  setImageName(rhelDefaults.imageName);
                                  setTag(rhelDefaults.tag);
                                }
                                setIsRegistryDropdownOpen(false);
                              }
                            } else {
                              setRegistryName('');
                              setSelectedRegistry(null);
                              setIsRegistryDropdownOpen(false);
                            }
                          }}
                          selected={registryName}
                          isOpen={isRegistryDropdownOpen}
                          onOpenChange={(isOpen) => setIsRegistryDropdownOpen(isOpen)}
                        >
                          <SelectList>
                            {ociRepositories.map((repo) => {
                              const registryUrl = repo.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
                              return (
                                <SelectOption
                                  key={repo.id}
                                  value={repo.name}
                                  description={registryUrl}
                                >
                                  {repo.name.toLowerCase()}
                                </SelectOption>
                              );
                            })}
                          </SelectList>
                        </Select>
                      </div>
                    </FlexItem>
                    <FlexItem>
                      <Button
                        variant="link"
                        isInline
                        icon={<PlusIcon />}
                        onClick={() => setShowCreateRegistryModal(true)}
                      >
                        Create a repository
                      </Button>
                    </FlexItem>
                  </Flex>
                  <FormHelperText>
                    Provide the full path to the image you want to use as an input (e.g., quay.io)
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              {/* Tag */}
              <StackItem>
                <FormGroup
                  label="Input image tag"
                  isRequired
                  fieldId="tag"
                >
                  <div className="app-wizard-field">
                    <TextInput
                      id="tag"
                      value={tag}
                      onChange={(_, value) => setTag(value)}
                    />
                  </div>
                </FormGroup>
              </StackItem>

              {/* Input image reference URL */}
              <StackItem>
                <FormGroup
                  label="Input image reference URL"
                  fieldId="source-image-url"
                >
                  <div style={{ width: '400px', maxWidth: '100%' }}>
                    {(() => {
                      const previewUrl = getInputImageReferenceUrl(selectedRegistry, imageName, tag);
                      
                      if (!previewUrl) {
                        return (
                          <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Not set</span>
                        );
                      }
                      
                      return (
                        <a
                          href={`https://${previewUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0066cc',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontFamily: 'monospace',
                            fontSize: 'var(--pf-t--global--font--size--sm)'
                          }}
                        >
                          {previewUrl}
                          <ExternalLinkAltIcon style={{ fontSize: '0.875rem', color: '#0066cc' }} />
                        </a>
                      );
                    })()}
                  </div>
                </FormGroup>
              </StackItem>
              </Stack>
            </div>
          </WizardStep>

          {/* Step 2: Image output */}
          <WizardStep name="Output image" id="step-output">
            <div className="form-container">
              <Stack hasGutter>
                <StackItem>
                  <Title headingLevel="h2" size="lg" className="form-section-title">Output image</Title>
                  <Content component="p" className="form-section-description" style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
                    Configure the output image and export formats for your build.
                  </Content>
                </StackItem>
                
                <StackItem>
                  <div className="app-wizard-field">
                    <Alert
                      variant="info"
                      isInline
                      className="app-info-alert"
                      title="Management-ready by default"
                    >
                      Includes agent for automatic management after deployment.
                    </Alert>
                  </div>
                </StackItem>
                
                {/* Output Repository */}
                <StackItem>
                  <FormGroup
                    label="Target repository"
                    isRequired
                    fieldId="output-repository"
                  >
                    {ociRepositories.length === 0 ? (
                      <Stack hasGutter>
                        <StackItem>
                          <Alert
                            variant={hasAdminPermission ? 'info' : 'warning'}
                            isInline
                            title={hasAdminPermission ? 'No output repositories configured' : 'No output repositories available'}
                          >
                            {hasAdminPermission ? (
                              <>
                                No OCI repositories are configured. You can create one to use as an output repository.
                                <div style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
                                  <Button
                                    variant="link"
                                    icon={<PlusIcon />}
                                    onClick={() => {
                                      // Navigate to create repository page
                                      const currentPath = window.location.pathname;
                                      if (currentPath.includes('/red-hat-manager/')) {
                                        navigate('/red-hat-manager/repositories/create');
                                      } else {
                                        navigate('/repositories/create');
                                      }
                                    }}
                                  >
                                    Create repository
                                  </Button>
                                </div>
                              </>
                            ) : (
                              'Please contact your administrator to configure an OCI repository for image outputs.'
                            )}
                          </Alert>
                        </StackItem>
                      </Stack>
                    ) : (
                      <Stack hasGutter>
                        <StackItem>
                          <div style={{ width: '400px' }}>
                            <Select
                              toggle={outputRepositoryToggle}
                              onSelect={(_, value) => {
                                if (value) {
                                  // Handle "Create a repository" option
                                  if (value === 'zzz-create-repository') {
                                    setIsOutputRepositoryDropdownOpen(false);
                                    setShowCreateOutputRegistryModal(true);
                                    return;
                                  }
                                  // If create output registry form is open, show confirmation modal
                                  if (showCreateOutputRegistryModal) {
                                    setPendingOutputRepositorySelection(value as string);
                                    setShowConfirmOutputRepositoryModal(true);
                                    setIsOutputRepositoryDropdownOpen(false);
                                  } else {
                                    const selectedRepo = ociRepositories.find(repo => repo.name === value);
                                    setOutputRepository(value as string);
                                    if (selectedRepo) {
                                      setSelectedOutputRegistry({
                                        url: selectedRepo.url,
                                        credential: selectedRepo.credential,
                                      });
                                    }
                                    setIsOutputRepositoryDropdownOpen(false);
                                  }
                                } else {
                                  setOutputRepository('');
                                  setSelectedOutputRegistry(null);
                                  setIsOutputRepositoryDropdownOpen(false);
                                }
                              }}
                              isOpen={isOutputRepositoryDropdownOpen}
                              onOpenChange={(isOpen: boolean) => setIsOutputRepositoryDropdownOpen(isOpen)}
                              selected={outputRepository}
                            >
                              {(() => {
                                const options: React.ReactElement[] = [];
                                ociRepositories.forEach((repo) => {
                                  options.push(
                                    <SelectOption key={repo.id} value={repo.name}>
                                      {repo.name}
                                    </SelectOption>
                                  );
                                });
                                // Add "Create a repository" as the last option
                                options.push(
                                  <SelectOption 
                                    key="create-repository"
                                    value="zzz-create-repository"
                                    className="pf-v6-c-select__menu-item--action"
                                    data-action-item="true"
                                  >
                                    <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                                      <FlexItem>
                                        <PlusIcon />
                                      </FlexItem>
                                      <FlexItem>
                                        Create a repository
                                      </FlexItem>
                                    </Flex>
                                  </SelectOption>
                                );
                                return options;
                              })()}
                            </Select>
                          </div>
                          <FormHelperText>
                            Storage location for your image.
                          </FormHelperText>
                        </StackItem>
                      </Stack>
                    )}
                  </FormGroup>
                </StackItem>

                {/* Output Image Name */}
                <StackItem>
                  <FormGroup
                    label="Output image name"
                    isRequired
                    fieldId="output-image-name"
                  >
                    <div className="app-wizard-field">
                      <TextInput
                        id="output-image-name"
                        value={outputImageName}
                        onChange={(_, value) => setOutputImageName(value)}
                        aria-describedby="output-image-name-helper"
                        validated={outputImageName && !isOutputImageNameValid ? 'error' : 'default'}
                      />
                    </div>
                    <FormHelperText id="output-image-name-helper">
                      Use lowercase letters and hyphens (for example, <code>edge-image-01</code>).
                    </FormHelperText>
                  </FormGroup>
                </StackItem>

                {/* Output Tag */}
                <StackItem style={{ marginBottom: '16px' }}>
                  <FormGroup
                    label="Output image tag"
                    isRequired
                    fieldId="output-tag"
                  >
                    <div className="app-wizard-field">
                      <TextInput
                        id="output-tag"
                        value={outputTag}
                        onChange={(_, value) => setOutputTag(value)}
                        aria-describedby="output-tag-helper"
                      />
                    </div>
                    <FormHelperText id="output-tag-helper">
                    </FormHelperText>
                  </FormGroup>
                </StackItem>

                {/* Image reference URL */}
                <StackItem style={{ marginBottom: '16px' }}>
                  <FormGroup
                    label="Output image reference URL"
                    fieldId="output-image-url"
                  >
                    <div style={{ width: '400px', maxWidth: '100%' }}>
                      {(() => {
                        const getOutputPreviewUrl = () => {
                          if (!selectedOutputRegistry || !outputImageName) {
                            return '';
                          }
                          const registryUrl = selectedOutputRegistry.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
                          const tag = autoGenerateTag ? 'auto' : (outputTag || 'latest');
                          return `${registryUrl}/${outputImageName}:${tag}`;
                        };
                        
                        const previewUrl = getOutputPreviewUrl();
                        
                        if (!previewUrl) {
                          return (
                            <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>Not set</span>
                          );
                        }
                        
                        return (
                          <a
                            href={`https://${previewUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#0066cc',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontFamily: 'monospace',
                              fontSize: 'var(--pf-t--global--font--size--sm)'
                            }}
                          >
                            {previewUrl}
                            <ExternalLinkAltIcon style={{ fontSize: '0.875rem', color: '#0066cc' }} />
                          </a>
                        );
                      })()}
                    </div>
                  </FormGroup>
                </StackItem>

              {/* Build disk images */}
              <StackItem>
                <FormGroup
                  label="Export formats"
                  fieldId="export-formats"
                >
                  <FormHelperText>
                    Choose formats you need for this image. Each selection will generate a separate, ready-to-use image file.
                  </FormHelperText>
                  <Flex style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }} gap={{ default: 'gapMd' }}>
                    {['iso', 'qcow2-container', 'vmdk', 'qcow2'].map((format) => {
                      const iconMap = {
                        'vmdk': <VirtualMachineIcon />,
                        'qcow2': <CloudSecurityIcon />,
                        'qcow2-container': <BuilderImageIcon />,
                        'iso': <ServerGroupIcon />
                      };
                      
                      return (
                        <FlexItem key={format}>
                          <Card
                            isClickable
                            onClick={() => handleExportFormatChange(format, !exportFormats.has(format))}
                            style={{
                              boxSizing: 'border-box',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              padding: '16px',
                              position: 'relative',
                              width: '235px',
                              height: '196px',
                              background: '#FFFFFF',
                              border: '1px solid #C7C7C7',
                              borderRadius: '16px',
                              cursor: 'pointer'
                            }}
                          >
                            {/* Checkbox in top right */}
                            <div 
                              style={{
                                position: 'absolute',
                                width: '32px',
                                height: '24px',
                                right: '16px',
                                top: '16px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                id={`export-${format}`}
                                isChecked={exportFormats.has(format)}
                                onChange={(_, checked) => {
                                  handleExportFormatChange(format, checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ margin: 0 }}
                                aria-label={`Select ${getExportFormatTitle(format)}`}
                              />
                            </div>
                            
                            {/* Card Content - Icon, Title, Description */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              padding: '0px',
                              gap: '16px',
                              width: '203px',
                              height: '135px',
                              flex: 'none',
                              order: 0,
                              flexGrow: 1
                            }}>
                              {/* Icon */}
                              <div style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 'none',
                                order: 0,
                                flexGrow: 0
                              }}>
                                <Icon size="xl">
                                  {iconMap[format]}
                                </Icon>
                              </div>
                              
                              {/* Title + Description */}
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                padding: '0px',
                                gap: '4px',
                                width: '203px',
                                flex: 'none',
                                order: 1,
                                alignSelf: 'stretch',
                                flexGrow: 0
                              }}>
                                {/* Title */}
                                <div style={{
                                  width: '203px',
                                  height: '24px',
                                  fontFamily: 'var(--pf-t--global--font--family--heading)',
                                  fontStyle: 'normal',
                                  fontWeight: 'var(--pf-t--global--font--weight--heading--md)',
                                  fontSize: 'var(--pf-t--global--font--size--md)',
                                  lineHeight: 'var(--pf-t--global--line-height--heading--md)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: 'var(--pf-t--global--text--color--default)',
                                  flex: 'none',
                                  order: 0,
                                  alignSelf: 'stretch',
                                  flexGrow: 0
                                }}>
                                  {getExportFormatTitle(format)}
                                </div>
                                
                                {/* Description */}
                                <div style={{
                                  width: '203px',
                                  fontFamily: 'var(--pf-t--global--font--family--body)',
                                  fontStyle: 'normal',
                                  fontWeight: 'var(--pf-t--global--font--weight--body)',
                                  fontSize: 'var(--pf-t--global--font--size--sm)',
                                  lineHeight: 'var(--pf-t--global--line-height--body)',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  color: 'var(--pf-t--global--text--color--subtle)',
                                  flex: 'none',
                                  order: 1,
                                  alignSelf: 'stretch',
                                  flexGrow: 0
                                }}>
                                  {getExportFormatDescription(format)}
                                </div>
                              </div>
                            </div>
                          </Card>
                        </FlexItem>
                      );
                    })}
                  </Flex>
                  {exportFormats.size > 0 && (
                    <div style={{
                      marginTop: 'var(--pf-t--global--spacer--sm)',
                      padding: 'var(--pf-t--global--spacer--sm)',
                      backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
                      borderRadius: '4px',
                      fontSize: 'var(--pf-t--global--font--size--sm)',
                      color: 'var(--pf-t--global--text--color--default)'
                    }}>
                      {exportFormats.size} {exportFormats.size === 1 ? 'image export task' : 'image export tasks'} will be created.
                    </div>
                  )}
                </FormGroup>
                </StackItem>
                <StackItem style={{ marginBottom: '24px' }}></StackItem>
              </Stack>
            </div>
          </WizardStep>

          {/* Step 3: Registration */}
          <WizardStep name="Registration" id="step-binding">
            <div className="form-container">
              <Stack hasGutter>
                <StackItem>
                  <Title headingLevel="h2" size="lg" className="form-section-title">Registration</Title>
                  <Content component="p" className="form-section-description" style={{ marginBottom: '0' }}>
                    Define the registry and base image for your system image.
                  </Content>
                </StackItem>

                {/* Registration - Radio Button Selection */}
                <StackItem>
                  <Stack hasGutter>
                    {/* Early Binding Option */}
                    <StackItem>
                      <Card
                        className={`app-binding-card${bindingType === 'early' ? ' app-binding-card--selected' : ''}`}
                        onClick={() => setBindingType('early')}
                      >
                        <CardBody>
                          <Stack hasGutter>
                            <StackItem>
                              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                                <FlexItem>
                                  <Radio
                                    id="binding-early"
                                    name="binding-type"
                                    label=""
                                    isChecked={bindingType === 'early'}
                                    onChange={() => setBindingType('early')}
                                  />
                                </FlexItem>
                                <FlexItem>
                                  <Title headingLevel="h3" size="md">Early binding</Title>
                                </FlexItem>
                              </Flex>
                            </StackItem>
                            <StackItem>
                              <Content>
                                Devices booting from this image auto-enroll. Register within 1 year to remain active.
                              </Content>
                            </StackItem>
                            
                            {/* Early Binding Options - Inside Card */}
                            {bindingType === 'early' && (
                              <>
                                <StackItem>
                                  <Divider />
                                </StackItem>
                                <StackItem>
                                  <Stack hasGutter>
                                    <StackItem>
                                      <DescriptionList isHorizontal>
                                        <DescriptionListGroup>
                                          <DescriptionListTerm>Enrollment</DescriptionListTerm>
                                          <DescriptionListDescription>Auto-create certificate</DescriptionListDescription>
                                        </DescriptionListGroup>
                                        <DescriptionListGroup>
                                          <DescriptionListTerm>Registration</DescriptionListTerm>
                                          <DescriptionListDescription>1 year (Standard)</DescriptionListDescription>
                                        </DescriptionListGroup>
                                      </DescriptionList>
                                    </StackItem>
                                  </Stack>
                                </StackItem>
                              </>
                            )}
                          </Stack>
                        </CardBody>
                      </Card>
                    </StackItem>

                    {/* Late Binding Option */}
                    <StackItem>
                      <Card
                        className={`app-binding-card${bindingType === 'late' ? ' app-binding-card--selected' : ''}`}
                        onClick={() => setBindingType('late')}
                      >
                        <CardBody>
                          <Stack hasGutter>
                            <StackItem>
                              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                                <FlexItem>
                                  <Radio
                                    id="binding-late"
                                    name="binding-type"
                                    label=""
                                    isChecked={bindingType === 'late'}
                                    onChange={() => setBindingType('late')}
                                  />
                                </FlexItem>
                                <FlexItem>
                                  <Title headingLevel="h3" size="md">Late binding</Title>
                                </FlexItem>
                              </Flex>
                            </StackItem>
                            <StackItem>
                              <Content>
                                Provisioning tools are enabled by default.
                              </Content>
                            </StackItem>
                          </Stack>
                        </CardBody>
                      </Card>
                    </StackItem>
                  </Stack>
                </StackItem>

                {/* SSH Public Key Section */}
                <StackItem style={{ marginTop: '16px' }}>
                  <Stack hasGutter>
                    <StackItem>
                      <Content 
                        component="p" 
                        style={{ 
                          fontFamily: 'var(--pf-t--global--font--family--body)',
                          fontWeight: 500,
                          fontSize: 'var(--pf-t--global--font--size--md)',
                          lineHeight: 'var(--pf-t--global--line-height--body)',
                          color: 'var(--pf-t--global--text--color--default)',
                          marginBottom: 0
                        }}
                      >
                        Remote access
                      </Content>
                    </StackItem>
                    <StackItem>
                      <Checkbox
                        id="enable-ssh-access"
                        label="Provide an SSH public key to enable passwordless login once your image is deployed."
                        isChecked={enableSshAccess}
                        onChange={(_, checked) => setEnableSshAccess(checked)}
                      />
                    </StackItem>
                    {enableSshAccess && (
                      <StackItem>
                        <FormGroup
                          label={
                            <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                              <FlexItem>SSH public key</FlexItem>
                              <FlexItem>
                                <Tooltip content="Get help with SSH public key">
                                  <QuestionCircleIcon style={{ fontSize: 'var(--pf-t--global--font--size--sm)', color: 'var(--pf-t--global--text--color--subtle)' }} />
                                </Tooltip>
                              </FlexItem>
                            </Flex>
                          }
                          fieldId="ssh-public-key"
                        >
                          <Stack hasGutter style={{ marginTop: 0 }}>
                            {/* File Input Section */}
                            <StackItem>
                              <InputGroup>
                                <InputGroupItem isFill>
                                  <TextInput
                                    id="ssh-key-filename"
                                    value={sshKeyFileName}
                                    onChange={(_, value) => setSshKeyFileName(value)}
                                    style={{ width: '100%' }}
                                  />
                                </InputGroupItem>
                                <InputGroupItem>
                                  <input
                                    type="file"
                                    accept=".pub,text/plain"
                                    style={{ display: 'none' }}
                                    id="ssh-key-file-input"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        setSshKeyFileName(file.name);
                                        const reader = new FileReader();
                                        reader.onload = (event) => {
                                          const content = event.target?.result as string;
                                          setSshPublicKey(content.trim());
                                        };
                                        reader.readAsText(file);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="primary"
                                    onClick={() => {
                                      document.getElementById('ssh-key-file-input')?.click();
                                    }}
                                  >
                                    Browse...
                                  </Button>
                                </InputGroupItem>
                                <InputGroupItem>
                                  <Button
                                    variant="secondary"
                                    onClick={() => {
                                      setSshKeyFileName('');
                                      setSshPublicKey('');
                                      // Reset file input
                                      const fileInput = document.getElementById('ssh-key-file-input') as HTMLInputElement;
                                      if (fileInput) {
                                        fileInput.value = '';
                                      }
                                    }}
                                    isDisabled={!sshKeyFileName && !sshPublicKey}
                                  >
                                    Clear
                                  </Button>
                                </InputGroupItem>
                              </InputGroup>
                            </StackItem>
                            
                            {/* TextArea for Key Content */}
                            <StackItem>
                              <TextArea
                                id="ssh-public-key"
                                value={sshPublicKey}
                                onChange={(_, value) => {
                                  setSshPublicKey(value);
                                  // Clear filename if user pastes directly
                                  if (value && !sshKeyFileName) {
                                    setSshKeyFileName('');
                                  }
                                }}
                                placeholder="Paste your public key here..."
                                rows={4}
                                resizeOrientation="vertical"
                                style={{
                                  fontFamily: 'var(--pf-t--global--font--family--monospace)',
                                  fontSize: 'var(--pf-t--global--font--size--sm)',
                                  border: `1px dashed var(--pf-t--global--color--nonstatus--gray--300)`
                                }}
                              />
                            </StackItem>
                          </Stack>
                          <FormHelperText>
                            Paste the content of a public ssh key you want to use to connect to the hosts into this field.{' '}
                            <Button
                              variant="link"
                              component="a"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                // TODO: Implement learn more link
                                console.log('Learn more about SSH public keys');
                              }}
                              icon={<ExternalLinkAltIcon />}
                              iconPosition="right"
                              isInline
                              style={{ padding: 0, fontSize: 'inherit', verticalAlign: 'baseline' }}
                            >
                              Learn more
                            </Button>
                          </FormHelperText>
                        </FormGroup>
                      </StackItem>
                    )}
                  </Stack>
                </StackItem>
              </Stack>
            </div>
          </WizardStep>

          {/* Step 4: Review */}
          <WizardStep name="Review" id="step-review" footer={{ nextButtonText: 'Build image' }}>
            <ReviewStepContent
              registryName={registryName}
              selectedRegistry={selectedRegistry}
              imageName={imageName}
              tag={tag}
              outputRepository={outputRepository}
              selectedOutputRegistry={selectedOutputRegistry}
              outputImageName={outputImageName}
              outputTag={outputTag}
              autoGenerateTag={autoGenerateTag}
              getOutputImagePreview={getOutputImagePreview}
              exportFormats={exportFormats}
              includeFlightControlAgent={includeFlightControlAgent}
              bindingType={bindingType}
              earlyRegistrationOption={earlyRegistrationOption}
              autoCreateCert={autoCreateCert}
              cert={cert}
              selectedCertificate={selectedCertificate}
              durationType={durationType}
              customExpirationDate={customExpirationDate}
              ttl={ttl}
              formatExpirationDate={formatExpirationDate}
            />
          </WizardStep>
              </Wizard>
            </DrawerContentBody>
          </DrawerContent>
        </Drawer>
      </PageSection>

      {/* Warning Modal - Data Loss Prevention */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showConfirmModal}
        onClose={handleCancelSelectRegistry}
      >
        <ModalHeader
          title="Discard registry creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you leave this flow to select an existing registry, the information you entered for the new registry will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmSelectRegistry}>
            Discard and select existing
          </Button>
          <Button key="keep" variant="secondary" onClick={handleCancelSelectRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Cancel Registry Creation) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelRegistryModal}
        onClose={handleKeepCreatingRegistry}
      >
        <ModalHeader
          title="Discard repository creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel creating this repository, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancelRegistry}>
            Discard and cancel
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepCreatingRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Output Repository Selection) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showConfirmOutputRepositoryModal}
        onClose={handleCancelSelectOutputRepository}
      >
        <ModalHeader
          title="Discard registry creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you leave this flow to select an existing registry, the information you entered for the new registry will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmSelectOutputRepository}>
            Discard and select existing
          </Button>
          <Button key="keep" variant="secondary" onClick={handleCancelSelectOutputRepository}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Cancel Output Registry Creation) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelOutputRepositoryModal}
        onClose={handleKeepCreatingOutputRegistry}
      >
        <ModalHeader
          title="Discard repository creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel creating this repository, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancelOutputRegistry}>
            Discard
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepCreatingOutputRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Registry Modal */}
      <Modal
        variant={ModalVariant.large}
        isOpen={showCreateRegistryModal}
        onClose={handleCancelRegistry}
      >
        <ModalHeader title="Create repository" />
        <ModalBody>
          <Form>
            <Stack hasGutter>
              <StackItem>
                <FormGroup
                  label="Repository name"
                  isRequired
                  fieldId="new-repository-name"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-repository-name"
                      value={newRegistryName}
                      onChange={(_, value) => setNewRegistryName(value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Registry hostname"
                  isRequired
                  fieldId="new-registry-hostname"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-registry-hostname"
                      value={newRegistryHostname}
                      onChange={(_, value) => setNewRegistryHostname(value)}
                      aria-describedby="new-registry-hostname-helper"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <FormHelperText id="new-registry-hostname-helper">
                    For example: quay.io, registry.redhat.io, myregistry.com:5000
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Repository type"
                  fieldId="new-repository-type"
                  isRequired
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-repo-type-git"
                        name="new-repository-type"
                        label="Git repository"
                        isChecked={newRegistryType === 'git'}
                        onChange={() => setNewRegistryType('git')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-repo-type-http"
                        name="new-repository-type"
                        label="HTTP service"
                        isChecked={newRegistryType === 'http'}
                        onChange={() => setNewRegistryType('http')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-repo-type-oci"
                        name="new-repository-type"
                        label="OCI registry"
                        isChecked={newRegistryType === 'oci'}
                        onChange={() => setNewRegistryType('oci')}
                      />
                    </StackItem>
                  </Stack>
                  <FormHelperText>
                    Only OCI registry type is available for image builds
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Scheme"
                  fieldId="new-scheme"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-scheme-https"
                        name="new-scheme"
                        label="HTTPS"
                        isChecked={newScheme === 'https'}
                        onChange={() => setNewScheme('https')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-scheme-http"
                        name="new-scheme"
                        label="HTTP"
                        isChecked={newScheme === 'http'}
                        onChange={() => setNewScheme('http')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Access mode"
                  fieldId="new-access-mode"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-access-readonly"
                        name="new-access-mode"
                        label="View"
                        isChecked={newAccessMode === 'readonly'}
                        onChange={() => setNewAccessMode('readonly')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-access-readwrite"
                        name="new-access-mode"
                        label="Manage"
                        isChecked={newAccessMode === 'readwrite'}
                        onChange={() => setNewAccessMode('readwrite')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Advanced configurations"
                  fieldId="new-advanced-configurations"
                >
                </FormGroup>
              </StackItem>

              <StackItem>
                <Checkbox
                  id="new-use-basic-auth"
                  label="Basic authentication"
                  isChecked={newUseBasicAuth}
                  onChange={(_, checked) => setNewUseBasicAuth(checked)}
                />
              </StackItem>

              {newUseBasicAuth && (
                <>
                  <StackItem>
                    <FormGroup
                      label="Username"
                      isRequired
                      fieldId="new-username"
                    >
                      <TextInput
                        id="new-username"
                        value={newUsername}
                        onChange={(_, value) => setNewUsername(value)}
                      />
                    </FormGroup>
                  </StackItem>

                  <StackItem>
                    <FormGroup
                      label="Password"
                      isRequired
                      fieldId="new-password"
                    >
                      <TextInput
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(_, value) => setNewPassword(value)}
                      />
                    </FormGroup>
                  </StackItem>
                </>
              )}

              <StackItem>
                <Checkbox
                  id="new-skip-server-verification"
                  label="Skip server verification"
                  isChecked={newSkipServerVerification}
                  onChange={(_, checked) => setNewSkipServerVerification(checked)}
                />
              </StackItem>

              <StackItem>
                <FormGroup
                  label="CA certificate"
                  fieldId="new-ca-certificate"
                >
                  <TextArea
                    id="new-ca-certificate"
                    value={newCaCertificate}
                    onChange={(_, value) => setNewCaCertificate(value)}
                    rows={6}
                    resizeOrientation="vertical"
                  />
                </FormGroup>
              </StackItem>
            </Stack>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="save"
            variant="primary"
            onClick={handleSaveRegistry}
            isDisabled={!isNewRegistryValid}
          >
            Save
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={handleCancelRegistry}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Red Hat Registry Modal */}
      <Modal
        variant={ModalVariant.medium}
        isOpen={showRedHatRegistryModal}
        onClose={() => {
          setShowRedHatRegistryModal(false);
          // Keep checkbox unchecked if user cancels
          setBaseImageCheckbox(false);
        }}
      >
        <ModalHeader title="Registry credentials" />
        <ModalBody>
          <Form>
            <Stack hasGutter>
              {redHatAuthError && (
                <StackItem>
                  <Alert variant="danger" isInline title="Authentication failed">
                    Verification of these credentials failed. Check your URL, username, and password, and try again.
                  </Alert>
                </StackItem>
              )}
              
              <StackItem>
                <Content>
                  <p>Provide credentials to sync registry data.</p>
                </Content>
              </StackItem>
              
              <StackItem>
                <FormGroup
                  label="Registry URL"
                  isRequired
                  fieldId="redhat-registry"
                >
                  <div style={{ width: '400px', maxWidth: '100%' }}>
                    <TextInput
                      id="redhat-registry"
                      value={redHatRegistry}
                      onChange={(_, value) => setRedHatRegistry(value)}
                      placeholder="quay.io"
                      style={{ width: '100%', textAlign: 'center' }}
                      className="aligned-text-input"
                    />
                  </div>
                  <FormHelperText>e.g., quay.io or registry.redhat.io</FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Authentication type"
                  isRequired
                  fieldId="redhat-auth-type"
                >
                  <div style={{ width: '400px', maxWidth: '100%' }}>
                    <Select
                      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                        <MenuToggle
                          ref={toggleRef}
                          onClick={() => setIsRedHatAuthTypeOpen(!isRedHatAuthTypeOpen)}
                          isExpanded={isRedHatAuthTypeOpen}
                          style={{ width: '100%', textAlign: 'center' }}
                        >
                          {redHatAuthType === 'basic' ? 'Basic authentication' : redHatAuthType === 'token' ? 'Token' : 'Select authentication type'}
                        </MenuToggle>
                      )}
                      onSelect={(_, value) => {
                        setRedHatAuthType(value as string);
                        setIsRedHatAuthTypeOpen(false);
                      }}
                      selected={redHatAuthType}
                      isOpen={isRedHatAuthTypeOpen}
                      onOpenChange={(isOpen) => setIsRedHatAuthTypeOpen(isOpen)}
                    >
                      <SelectOption value="basic">Basic authentication</SelectOption>
                      <SelectOption value="token">Token</SelectOption>
                    </Select>
                  </div>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Username"
                  isRequired
                  fieldId="redhat-username"
                >
                  <div style={{ width: '400px', maxWidth: '100%' }}>
                    <TextInput
                      id="redhat-username"
                      value={redHatUsername}
                      onChange={(_, value) => setRedHatUsername(value)}
                      placeholder="Enter account or service ID"
                      style={{ width: '100%', textAlign: 'center' }}
                      className="aligned-text-input"
                    />
                  </div>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label={redHatAuthType === 'token' ? 'Token' : 'Password or token'}
                  isRequired
                  fieldId="redhat-password"
                >
                  <div style={{ width: '400px', maxWidth: '100%' }}>
                    <InputGroup>
                      <InputGroupItem isFill>
                        <TextInput
                          id="redhat-password"
                          type={showRedHatPassword ? 'text' : 'password'}
                          value={redHatPassword}
                          onChange={(_, value) => {
                            setRedHatPassword(value);
                            setRedHatAuthError(false); // Clear error when user types
                          }}
                          placeholder={redHatAuthType === 'token' ? 'Enter the authentication token' : 'Enter the password or authentication token'}
                          style={{ width: '100%', textAlign: 'center' }}
                          className="aligned-text-input"
                        />
                      </InputGroupItem>
                      <InputGroupItem>
                        <Button
                          variant="control"
                          aria-label={showRedHatPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowRedHatPassword(!showRedHatPassword)}
                        >
                          {showRedHatPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </Button>
                      </InputGroupItem>
                    </InputGroup>
                  </div>
                </FormGroup>
              </StackItem>
            </Stack>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="connect"
            variant="primary"
            onClick={() => {
              // Validate all three required fields are filled (URL, Username, Password)
              if (redHatRegistry && redHatUsername && redHatPassword) {
                // TODO: Implement actual authentication logic
                // For now, simulate authentication
                // In real implementation, this would call an API
                const authSuccess = true; // Replace with actual auth check
                
                if (authSuccess) {
                  // Store registry name for toast message before clearing
                  const registryNameForToast = redHatRegistry || 'quay.io';
                  setConnectedRegistryName(registryNameForToast);
                  
                  // Close modal immediately upon success
                  setShowRedHatRegistryModal(false);
                  setBaseImageCheckbox(true);
                  setRedHatAuthError(false);
                  
                  // Clear form fields after successful connection (saves to state)
                  setRedHatRegistry('');
                  setRedHatAuthType('');
                  setRedHatUsername('');
                  setRedHatPassword('');
                  setShowRedHatPassword(false);
                  
                  // Show success toast notification (appears after modal closes)
                  // Use setTimeout to ensure modal is closed before showing toast
                  setTimeout(() => {
                    setShowRegistryConnectedToast(true);
                    // Auto-dismiss after 8 seconds
                    setTimeout(() => {
                      setShowRegistryConnectedToast(false);
                    }, 8000);
                  }, 100);
                  // TODO: Save credentials logic here
                } else {
                  setRedHatAuthError(true);
                }
              }
            }}
            isDisabled={
              !redHatRegistry || redHatRegistry.trim().length === 0 ||
              !redHatUsername || redHatUsername.trim().length === 0 ||
              !redHatPassword || redHatPassword.trim().length === 0
            }
          >
            Connect
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={() => {
              setShowRedHatRegistryModal(false);
              // Keep checkbox unchecked if user cancels
              setBaseImageCheckbox(false);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Output Registry Modal */}
      <Modal
        variant={ModalVariant.large}
        isOpen={showCreateOutputRegistryModal}
        onClose={handleCancelOutputRegistry}
      >
        <ModalHeader title="Create repository" />
        <ModalBody>
          <Form>
            <Stack hasGutter>
              <StackItem>
                <FormGroup
                  label="Repository name"
                  isRequired
                  fieldId="new-output-repository-name"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-output-repository-name"
                      value={newOutputRegistryName}
                      onChange={(_, value) => setNewOutputRegistryName(value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Registry hostname"
                  isRequired
                  fieldId="new-output-registry-hostname"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-output-registry-hostname"
                      value={newOutputRegistryHostname}
                      onChange={(_, value) => setNewOutputRegistryHostname(value)}
                      aria-describedby="new-output-registry-hostname-helper"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <FormHelperText id="new-output-registry-hostname-helper">
                    For example: quay.io, registry.redhat.io, myregistry.com:5000
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Repository type"
                  fieldId="new-output-repository-type"
                  isRequired
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-output-repo-type-git"
                        name="new-output-repository-type"
                        label="Git repository"
                        isChecked={newOutputRegistryType === 'git'}
                        onChange={() => setNewOutputRegistryType('git')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-output-repo-type-http"
                        name="new-output-repository-type"
                        label="HTTP service"
                        isChecked={newOutputRegistryType === 'http'}
                        onChange={() => setNewOutputRegistryType('http')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-output-repo-type-oci"
                        name="new-output-repository-type"
                        label="OCI registry"
                        isChecked={newOutputRegistryType === 'oci'}
                        onChange={() => setNewOutputRegistryType('oci')}
                      />
                    </StackItem>
                  </Stack>
                  <FormHelperText>
                    Only OCI registry type is available for image builds
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Scheme"
                  fieldId="new-output-scheme"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-output-scheme-https"
                        name="new-output-scheme"
                        label="HTTPS"
                        isChecked={newOutputScheme === 'https'}
                        onChange={() => setNewOutputScheme('https')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-output-scheme-http"
                        name="new-output-scheme"
                        label="HTTP"
                        isChecked={newOutputScheme === 'http'}
                        onChange={() => setNewOutputScheme('http')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Access mode"
                  fieldId="new-output-access-mode"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-output-access-readonly"
                        name="new-output-access-mode"
                        label="View"
                        isChecked={newOutputAccessMode === 'readonly'}
                        onChange={() => setNewOutputAccessMode('readonly')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-output-access-readwrite"
                        name="new-output-access-mode"
                        label="Manage"
                        isChecked={newOutputAccessMode === 'readwrite'}
                        onChange={() => setNewOutputAccessMode('readwrite')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Advanced configurations"
                  fieldId="new-output-advanced-configurations"
                >
                </FormGroup>
              </StackItem>

              <StackItem>
                <Checkbox
                  id="new-output-use-basic-auth"
                  label="Basic authentication"
                  isChecked={newOutputUseBasicAuth}
                  onChange={(_, checked) => setNewOutputUseBasicAuth(checked)}
                />
              </StackItem>

              {newOutputUseBasicAuth && (
                <>
                  <StackItem>
                    <FormGroup
                      label="Username"
                      isRequired
                      fieldId="new-output-username"
                    >
                      <TextInput
                        id="new-output-username"
                        value={newOutputUsername}
                        onChange={(_, value) => setNewOutputUsername(value)}
                      />
                    </FormGroup>
                  </StackItem>

                  <StackItem>
                    <FormGroup
                      label="Password"
                      isRequired
                      fieldId="new-output-password"
                    >
                      <TextInput
                        id="new-output-password"
                        type="password"
                        value={newOutputPassword}
                        onChange={(_, value) => setNewOutputPassword(value)}
                      />
                    </FormGroup>
                  </StackItem>
                </>
              )}

              <StackItem>
                <Checkbox
                  id="new-output-skip-server-verification"
                  label="Skip server verification"
                  isChecked={newOutputSkipServerVerification}
                  onChange={(_, checked) => setNewOutputSkipServerVerification(checked)}
                />
              </StackItem>

              <StackItem>
                <FormGroup
                  label="CA certificate"
                  fieldId="new-output-ca-certificate"
                >
                  <TextArea
                    id="new-output-ca-certificate"
                    value={newOutputCaCertificate}
                    onChange={(_, value) => setNewOutputCaCertificate(value)}
                    rows={6}
                    resizeOrientation="vertical"
                  />
                </FormGroup>
              </StackItem>
            </Stack>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="save"
            variant="primary"
            onClick={handleSaveOutputRegistry}
            isDisabled={!isNewOutputRegistryValid}
          >
            Save
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={handleCancelOutputRegistry}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Registry Creation Success Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showRegistrySuccessModal}
        onClose={() => setShowRegistrySuccessModal(false)}
      >
        <ModalHeader 
          title="Repository created successfully"
          titleIconVariant="success"
        />
        <ModalBody>
          <Content component="p">
            The repository <strong>"{createdRegistryName}"</strong> has been created and is now available in the registry dropdown and repositories table. It has been automatically selected for use.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button
            key="close"
            variant="primary"
            onClick={() => setShowRegistrySuccessModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Output Registry Creation Success Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showOutputRegistrySuccessModal}
        onClose={() => setShowOutputRegistrySuccessModal(false)}
      >
        <ModalHeader 
          title="Repository created successfully"
          titleIconVariant="success"
        />
        <ModalBody>
          <Content component="p">
            The repository <strong>"{createdOutputRegistryName}"</strong> has been created and is now available in the registry dropdown and repositories table. It has been automatically selected for use.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button
            key="close"
            variant="primary"
            onClick={() => setShowOutputRegistrySuccessModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Build Started Success Modal */}
      <Modal
        variant={ModalVariant.medium}
        isOpen={showBuildStartedModal}
        onClose={handleCloseBuildStartedModal}
      >
        <ModalHeader title="Build started" />
        <ModalBody>
          <Content component="p">
            Your image build is in progress. You can track the status in the image list.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button
            key="view-build-status"
            variant="primary"
            onClick={handleViewBuildStatus}
          >
            View build status
          </Button>
          <Button
            key="build-another"
            variant="secondary"
            onClick={handleBuildAnotherImage}
          >
            Build another image
          </Button>
          <Button
            key="close"
            variant="link"
            onClick={handleCloseBuildStartedModal}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Success Toast Notification - Registry Connected */}
      {showRegistryConnectedToast && (
        <div
          style={{
            position: 'fixed',
            top: 'var(--pf-t--global--spacer--md)',
            right: 'var(--pf-t--global--spacer--md)',
            zIndex: 10000,
            maxWidth: '400px'
          }}
        >
          <AlertGroup isToast>
            <Alert
              variant="success"
              title="Registry connected"
              actionClose={
                <AlertActionCloseButton
                  onClose={() => setShowRegistryConnectedToast(false)}
                />
              }
            >
              Credentials for {connectedRegistryName || 'quay.io'} were successfully verified and synced.
            </Alert>
          </AlertGroup>
        </div>
      )}
    </>
  );
};

export { CreateImageBuild };

